import { Gauge, LayoutDashboard, ListOrdered, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";

import { Page } from "@/components/page";
import { type PageTab, PageTabs } from "@/components/page-tabs";
import { ANALYTICS_PATH, CALLS_PATH, FAILURES_PATH, SPEED_PATH } from "@/lib/issues";
import { parsePeriod } from "@/lib/period";

import { OverviewPage } from "./overview";
import { movedHealthView, periodSearch } from "./utils";

export { CallsPage } from "./calls";
export { FailuresPage } from "./failures";
export { IssuePage } from "./issue";
export { SpeedPage } from "./speed";

/**
 * Statistieken, a layout route under `/analytics`: its title and a row of
 * tabs for its four parts, Overzicht, Oproepen, Fouten (with an issue's page
 * under it) and Snelheid, then the part that is open. Each part opens with
 * its own filter bar, its period included. A tab keeps the period the page
 * shows (`?days=30`); a custom time range stays on Oproepen.
 */
export function AnalyticsLayout() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const search = periodSearch(parsePeriod(params.get("days"))) || undefined;
  const tabs: PageTab[] = [
    { to: ANALYTICS_PATH, label: t("analytics.tabs.overview"), icon: LayoutDashboard, end: true },
    { to: CALLS_PATH, label: t("analytics.tabs.calls"), icon: ListOrdered },
    { to: FAILURES_PATH, label: t("analytics.tabs.failures"), icon: TriangleAlert },
    { to: SPEED_PATH, label: t("analytics.tabs.speed"), icon: Gauge },
  ].map((tab) => ({ ...tab, search }));

  return (
    <Page title={t("nav.analytics")} description={t("analytics.description")}>
      <PageTabs label={t("nav.analytics")} tabs={tabs} className="-mt-2 mb-6" />
      <Outlet />
    </Page>
  );
}

/**
 * Statistieken's first part, Overzicht, unless the address is the old health
 * view's (`/analytics?view=health`, from a bookmark or an old home page),
 * which now lives on Fouten.
 */
export function AnalyticsIndex() {
  const { search } = useLocation();
  const moved = movedHealthView(search);
  return moved ? <Navigate to={moved} replace /> : <OverviewPage />;
}
